package com.schoolconnect.app.feature.examresults

data class ExamSubjectMarkUi(
    val subject: String,
    val internal: Int?,
    val main: Int?,
    val total: Int?,
    val internalMax: Int = 20,
    val mainMax: Int = 80,
    val totalMax: Int = 100
)

data class ExamStudentResultUi(
    val rollNo: String,
    val name: String,
    val totalMarks: Int?,
    val totalPercentage: String,
    val grade: String,
    val rank: String,
    val note: String = "",
    val subjects: List<ExamSubjectMarkUi>
)

data class ExamGradeDistributionUi(val grade: String, val students: Int)

data class ExamSubjectCompletionUi(
    val subject: String,
    val completed: Int,
    val pending: Int,
    val completionPercentage: String
)

data class ExamResultDetailsUi(
    val totalStudents: Int,
    val completedStudents: Int,
    val pendingStudents: Int,
    val highestMarks: String,
    val lowestMarks: String,
    val classAverage: String,
    val highestPercentage: String,
    val averagePercentage: String,
    val passPercentage: String,
    val gradeDistribution: List<ExamGradeDistributionUi>,
    val subjectCompletion: List<ExamSubjectCompletionUi>,
    val students: List<ExamStudentResultUi>
)

private val dummySubjects = listOf("English", "Mathematics", "Science", "Social Science", "Hindi")

private fun student(
    rollNo: String,
    name: String,
    totalMarks: Int?,
    percentage: String,
    grade: String,
    rank: String,
    marks: List<List<Int?>>
) = ExamStudentResultUi(
    rollNo = rollNo,
    name = name,
    totalMarks = totalMarks,
    totalPercentage = percentage,
    grade = grade,
    rank = rank,
    subjects = dummySubjects.mapIndexed { index, subject ->
        val values = marks[index]
        ExamSubjectMarkUi(subject, values[0], values[1], values[2])
    }
)

private val baseDummyStudents = listOf(
    student("01", "Aarav Sharma", 447, "89.4%", "A+", "1", listOf(listOf(18, 70, 88), listOf(19, 76, 95), listOf(17, 74, 91), listOf(16, 68, 84), listOf(18, 71, 89))),
    student("02", "Diya Rao", 388, "77.6%", "A", "5", listOf(listOf(16, 60, 76), listOf(18, 63, 81), listOf(15, 64, 79), listOf(14, 60, 74), listOf(17, 61, 78))),
    student("03", "Rahul Mehta", null, "–", "–", "", listOf(listOf(null, null, null), listOf(18, 66, 84), listOf(17, 71, 88), listOf(16, 65, 81), listOf(18, 68, 86))),
    student("04", "Sneha Rao", 290, "58.0%", "C", "28", listOf(listOf(12, 48, 60), listOf(13, 50, 63), listOf(11, 49, 60), listOf(12, 46, 58), listOf(14, 52, 66))),
    student("05", "Aarush Patel", 345, "69.0%", "B", "12", listOf(listOf(15, 55, 70), listOf(14, 55, 69), listOf(13, 58, 71), listOf(13, 50, 63), listOf(16, 56, 72))),
    student("06", "Meera Joshi", 248, "49.6%", "C", "35", listOf(listOf(10, 38, 48), listOf(12, 40, 52), listOf(10, 38, 48), listOf(11, 40, 51), listOf(13, 48, 61))),
    student("07", "Arjun Nair", 238, "47.6%", "C", "37", listOf(listOf(8, 36, 44), listOf(12, 38, 50), listOf(9, 36, 45), listOf(10, 36, 46), listOf(11, 42, 53))),
    student("08", "Pooja Singh", 226, "45.2%", "C", "40", listOf(listOf(9, 34, 43), listOf(11, 36, 47), listOf(9, 35, 44), listOf(9, 34, 43), listOf(10, 40, 50)))
)

internal fun dummyExamResultDetails(row: ExamResultRowUi): ExamResultDetailsUi {
    // Result rows are scoped to an exam/class pair. Using only examId would
    // make every class under the same exam show identical result details.
    val resultNumber = row.id.filter(Char::isDigit).toIntOrNull() ?: 0
    val students = examStudentsFor(resultNumber)
    val published = row.status == "Published"
    val notStarted = row.status == "Not Started"
    val completed = when {
        published -> 42
        notStarted -> 0
        else -> 19
    }
    val markedStudents = students.mapNotNull { it.totalMarks }
    val highest = markedStudents.maxOrNull() ?: 0
    val lowest = markedStudents.minOrNull() ?: 0
    val average = if (markedStudents.isEmpty()) 0 else markedStudents.sum() / markedStudents.size
    val variant = resultNumber % 5
    val gradeCounts = listOf(5, 12, 11, 8, 4, 2)
    val gradeDistribution = gradeCounts.indices.map { index ->
        ExamGradeDistributionUi(
            listOf("A+", "A", "B+", "B", "C", "D")[index],
            gradeCounts[(index + variant) % gradeCounts.size]
        )
    }
    return ExamResultDetailsUi(
        totalStudents = 42,
        completedStudents = completed,
        pendingStudents = 42 - completed,
        highestMarks = "${(highest + variant * 3).coerceAtMost(500)} / 500",
        lowestMarks = "${(lowest - variant * 2).coerceAtLeast(0)} / 500",
        classAverage = "${average + variant * 2}.0 / 500",
        highestPercentage = percentage((highest + variant * 3).coerceAtMost(500)),
        averagePercentage = percentage((average + variant * 2).coerceAtMost(500)),
        passPercentage = if (published) "${90 + variant}.0%" else "–",
        gradeDistribution = gradeDistribution,
        subjectCompletion = dummySubjects.map { subject ->
            ExamSubjectCompletionUi(subject, completed, 42 - completed, if (completed == 0) "0%" else "${(completed * 100) / 42}%")
        },
        students = students
    )
}

private fun examStudentsFor(resultNumber: Int): List<ExamStudentResultUi> {
    val variant = resultNumber % 7
    return baseDummyStudents.mapIndexed { studentIndex, student ->
        val subjects = student.subjects.mapIndexed { subjectIndex, subject ->
            if (subject.internal == null || subject.main == null) {
                subject
            } else {
                val internal = (subject.internal + variant + studentIndex + subjectIndex) % (subject.internalMax + 1)
                val main = (subject.main + variant * 2 + studentIndex * 2 + subjectIndex) % (subject.mainMax + 1)
                subject.copy(internal = internal, main = main, total = internal + main)
            }
        }
        val total = if (subjects.all { it.total != null }) subjects.sumOf { it.total ?: 0 } else null
        val percentage = total?.let(::percentage) ?: "–"
        student.copy(
            totalMarks = total,
            totalPercentage = percentage,
            grade = total?.let(::gradeFor) ?: "–",
            subjects = subjects
        )
    }
}

private fun percentage(totalMarks: Int): String {
    val tenths = (totalMarks * 1000) / 500
    return "${tenths / 10}.${tenths % 10}%"
}

private fun gradeFor(totalMarks: Int): String = when {
    totalMarks >= 450 -> "A+"
    totalMarks >= 400 -> "A"
    totalMarks >= 350 -> "B+"
    totalMarks >= 300 -> "B"
    totalMarks >= 250 -> "C"
    else -> "D"
}
